'use client';

import * as React from 'react';
import {createPortal} from 'react-dom';
import {cn} from '@purpose/ui/lib/utils';

export interface PopupMenuProps {
  children: React.ReactNode;
  anchorEl?: HTMLElement | null;
  open?: boolean;
  onClose?: () => void;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right';
  };
  marginTop?: string | number;
  minWidth?: number;
  id?: string;
  slotProps?: {
    paper?: {
      sx?: any;
    };
  };
}

const PopupMenu = ({
  children,
  anchorEl,
  open = false,
  onClose,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right'
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'right'
  },
  marginTop,
  minWidth = 160,
  id,
  slotProps,
  ...props
}: PopupMenuProps) => {
  const [position, setPosition] = React.useState({top: 0, left: 0});
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (anchorEl && open) {
      const rect = anchorEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = rect.bottom;
      let left = rect.right;

      if (anchorOrigin.vertical === 'top') {
        top = rect.top;
      }
      if (anchorOrigin.horizontal === 'left') {
        left = rect.left;
      }

      // Adjust position to keep menu within viewport
      const menuWidth = minWidth || 160;
      const menuHeight = 200; // Estimated height

      // Adjust horizontal position if menu would overflow right edge
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 16; // 16px margin from edge
      }

      // Adjust vertical position if menu would overflow bottom edge
      if (top + menuHeight > viewportHeight) {
        top = rect.top - menuHeight - 8; // Show above instead
      }

      // Ensure minimum margins from viewport edges
      left = Math.max(16, left);
      top = Math.max(16, top);

      setPosition({top, left});
    }
  }, [anchorEl, open]);

  // Handle click outside to close menu
  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        if (onClose) {
          onClose();
        }
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, anchorEl, onClose]);

  if (!open || !anchorEl) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className={cn(
        'min-w-[160px] rounded-md border border-gray-200 bg-white shadow-lg',
        'animate-in fade-in-0 zoom-in-95'
      )}
      style={{
        position: 'fixed',
        top: position.top + (marginTop ? Number(marginTop) : 8),
        left: position.left,
        minWidth: minWidth,
        zIndex: 9999,
        pointerEvents: 'auto', // Ensure touch events work
        ...slotProps?.paper?.sx
      }}
      {...props}
    >
      {children}
    </div>,
    document.body
  );
};

export {PopupMenu};
